import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    TransactionReviewField,
    type FieldReviewFieldValue,
} from "@common/types/transactionFieldReview";
import { CategoryIconFromTaxonomy } from "@components/transactions/CategoryIcon";
import {
    ClassAvatarGroup,
    type ClassAvatarGroupClass,
} from "@components/avatar/ClassAvatarGroup";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { FieldReviewDetailsRow } from "../fieldReviewDetailsViewConfig";
import styles from "./FieldReviewCells.module.scss";

type FieldValueAccessor =
    | FieldReviewFieldValue
    | FieldReviewFieldValue[]
    | null
    | undefined;

export const FieldReviewValueCell = memo(
    ({
        getValue,
        row,
    }: CellContext<
        ViewTableFeatures,
        FieldReviewDetailsRow,
        FieldValueAccessor
    >) => {
        const value = getValue();
        const { reviewedField } = row.original;

        const classDisplay = useMemo(() => {
            if (
                reviewedField !== TransactionReviewField.CLASS ||
                !Array.isArray(value) ||
                value.length === 0
            ) {
                return null;
            }
            const classObjects: ClassAvatarGroupClass[] = value.map((v) => ({
                id: v.id,
                label: v.name,
                iconColor: v.iconColor ?? "#999999",
                order: 0,
            }));
            return <ClassAvatarGroup classes={classObjects} max={3} />;
        }, [reviewedField, value]);

        if (!value) {
            return (
                <ViewTableCell>
                    <span className={styles.valuePlaceholder}>-</span>
                </ViewTableCell>
            );
        }

        if (classDisplay) {
            return <ViewTableCell>{classDisplay}</ViewTableCell>;
        }

        if (Array.isArray(value)) {
            return (
                <ViewTableCell>
                    <span className={styles.valueText}>
                        {value.map((v) => v.name).join(", ")}
                    </span>
                </ViewTableCell>
            );
        }

        if (reviewedField === TransactionReviewField.CATEGORY) {
            return (
                <ViewTableCell>
                    <span className={styles.valueWithIcon}>
                        {value.taxonomy && (
                            <CategoryIconFromTaxonomy
                                taxonomy={value.taxonomy}
                            />
                        )}
                        <span className={styles.valueText}>{value.name}</span>
                    </span>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <span className={styles.valueText}>{value.name}</span>
            </ViewTableCell>
        );
    },
);

FieldReviewValueCell.displayName = "FieldReviewValueCell";
